import React from 'react';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateInputField } from './DateInputField';
import { DriverSaveRequest } from '../types/driver';

interface DriverFormFieldsProps {
  value: DriverSaveRequest;
  disabled?: boolean;
  onChange: (nextValue: DriverSaveRequest) => void;
  showStatus?: boolean;
  layout?: 'default' | 'compact';
}

export const DriverFormFields: React.FC<DriverFormFieldsProps> = ({
  value,
  disabled = false,
  onChange,
  showStatus = true,
  layout = 'default',
}) => {
  const { t } = useTranslation();
  const compact = layout === 'compact';
  const fieldGrid = compact ? { xs: 12, sm: 6, md: 3 } : { xs: 12, sm: 6 };

  const updateField = <K extends keyof DriverSaveRequest>(
    field: K,
    fieldValue: DriverSaveRequest[K]
  ) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <Grid container spacing={compact ? 1 : 1.5}>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          required
          disabled={disabled}
          label={t('fields.firstName')}
          value={value.firstName}
          onChange={(event) => updateField('firstName', event.target.value)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          required
          disabled={disabled}
          label={t('fields.lastName')}
          value={value.lastName}
          onChange={(event) => updateField('lastName', event.target.value)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.personalIdNumber')}
          value={value.personalIdNumber || ''}
          onChange={(event) => updateField('personalIdNumber', event.target.value)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <DateInputField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.dateOfBirth')}
          value={value.dateOfBirth || ''}
          onChange={(nextValue) => updateField('dateOfBirth', nextValue)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          required
          disabled={disabled}
          label={t('fields.phoneNumber')}
          value={value.phoneNumber}
          onChange={(event) => updateField('phoneNumber', event.target.value)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          type="email"
          disabled={disabled}
          label={t('fields.email')}
          value={value.email || ''}
          onChange={(event) => updateField('email', event.target.value)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.licenseNumber')}
          value={value.driverLicenseNumber || ''}
          onChange={(event) => updateField('driverLicenseNumber', event.target.value)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <DateInputField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.licenseIssueDate', 'License Issue Date')}
          value={value.licenseIssueDate || ''}
          onChange={(nextValue) => updateField('licenseIssueDate', nextValue)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <DateInputField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.expiryDate')}
          value={value.licenseExpiryDate || ''}
          onChange={(nextValue) => updateField('licenseExpiryDate', nextValue)}
        />
      </Grid>
      <Grid item {...fieldGrid}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          label={t('fields.licenseCategories', 'License Categories')}
          value={value.licenseCategories || ''}
          onChange={(event) => updateField('licenseCategories', event.target.value)}
        />
      </Grid>
      {showStatus ? (
        <Grid item {...fieldGrid}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>{t('fields.status')}</InputLabel>
            <Select
              label={t('fields.status')}
              value={value.status || 'ACTIVE'}
              onChange={(event) => updateField('status', event.target.value as DriverSaveRequest['status'])}
            >
              <MenuItem value="ACTIVE">{t('common.active', 'Active')}</MenuItem>
              <MenuItem value="INACTIVE">{t('common.inactive', 'Inactive')}</MenuItem>
              <MenuItem value="SUSPENDED">{t('fields.suspended', 'Suspended')}</MenuItem>
              <MenuItem value="BANNED">{t('fields.banned', 'Banned')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          disabled={disabled}
          multiline
          minRows={compact ? 1 : 2}
          label={t('fields.notes')}
          value={value.notes || ''}
          onChange={(event) => updateField('notes', event.target.value)}
        />
      </Grid>
    </Grid>
  );
};
